/**
 * The cook view (#1324) — one step at a time, at the stove.
 *
 * ⚠️ THE SCREEN MUST NOT SLEEP. A recipe that locks itself halfway through a
 * step, in front of somebody with wet hands, is a recipe that gets abandoned.
 * Wake Lock where it exists; nothing pretends where it does not.
 *
 * Everything here is bigger than the rest of the app on purpose. This is the
 * one screen read from two feet away.
 */
import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { twoPlates, type CookStep } from "../api/twoPlates";
import { GatherCard } from "../components/GatherCard";
import { MealMedia } from "../components/MealMedia";

export default function Cook() {
  const { mealId } = useParams();
  const navigate = useNavigate();
  const id = Number(mealId);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["method", id],
    queryFn: () => twoPlates.method(id),
    enabled: Number.isInteger(id),
  });

  const [index, setIndex] = useState(0);
  const [phase, setPhase] = useState<"morning" | "evening" | null>(null);
  useKeepAwake();

  const method = data?.method;
  const lastTime = data?.lastTime ?? null;
  const [dismissedNote, setDismissedNote] = useState(false);

  // A slow cooker is two visits to the kitchen, so it opens on a choice rather
  // than dropping somebody into step one of a thing they did eight hours ago.
  const steps: CookStep[] =
    !method ? []
    : method.slowCooker && phase === "morning" ? method.morningSteps
    : method.slowCooker && phase === "evening" ? method.eveningSteps
    : method.steps;

  if (isLoading) return <p className="text-muted text-sm">Loading the method…</p>;
  if (isError || !method) return <p className="text-warn text-sm">No method for that meal.</p>;

  if (method.slowCooker && phase === null) {
    return (
      <div>
        {/* ⚠️ break-words on every long string in this file. These screens are
            read on a phone in a kitchen, where the card is about 288px wide —
            a meal name or an ingredient that will not wrap pushes the whole
            page sideways. */}
        <h1 className="break-words text-2xl font-semibold tracking-tight">{method.mealName}</h1>
        <p className="text-muted mt-2 text-sm">
          This one is a slow cooker. That means two trips to the kitchen. Which
          one are you on?
        </p>
        <div className="mt-6 flex flex-col gap-3">
          <button
            onClick={() => { setPhase("morning"); setIndex(0); }}
            className="bg-plate-a text-ground tap w-full rounded-lg font-semibold"
          >
            Morning — {method.morningSteps.length} steps
          </button>
          <button
            onClick={() => { setPhase("evening"); setIndex(0); }}
            className="border-line tap w-full rounded-lg border font-medium"
          >
            Evening — {method.eveningSteps.length} steps
          </button>
        </div>
      </div>
    );
  }

  /**
   * Card zero is "get this out" (#1338), so every step shifts by one.
   *
   * ⛔ NOT SHOWN IN A SLOW COOKER'S EVENING. The pot went on at eight; listing
   * the beans again at six would be telling somebody to fetch something they
   * used this morning. What is left in the evening is the forks, and the fork
   * steps already carry those.
   */
  const showGather = !(method.slowCooker && phase === "evening");
  const offset = showGather ? 1 : 0;
  const totalCards = steps.length + offset;
  const onGather = showGather && index === 0;
  const step = steps[index - offset];
  const done = index >= totalCards;

  if (done) {
    return (
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">That is dinner.</h1>
        <p className="text-muted mt-2 break-words text-sm">{method.mealName}, on the table.</p>

        {/* ⚠️ Asked HERE, and only here. This is the one moment somebody knows
            what to write down, and the only moment they will bother. */}
        <NoteForNextTime mealId={id} existing={lastTime} />

        {/* ⭐ The same reasoning as the note above, applied to the photo: the
            food is on the table NOW. Ten minutes later it is eaten and nobody
            photographs an empty pan. Past attempts show here too, so this is
            also where you see how it turned out last time. */}
        <MealMedia mealId={id} />

        <button
          onClick={() => navigate("/plan")}
          className="bg-plate-b text-ground tap mt-6 w-full rounded-lg font-semibold"
        >
          Back to the plan
        </button>
        <button
          onClick={() => setIndex(0)}
          className="text-muted mt-4 text-sm underline underline-offset-4"
        >
          Start again
        </button>
      </div>
    );
  }

  return (
    <div className="flex min-h-[70dvh] flex-col">
      {lastTime && index === 0 && !dismissedNote && (
        <div
          className="mb-6 rounded-xl border p-4"
          style={{ borderColor: "var(--color-plate-a)" }}
        >
          <p className="text-plate-a text-xs uppercase tracking-wide">Last time you said</p>
          <p className="mt-1.5 text-base">{lastTime}</p>
          <button
            onClick={() => setDismissedNote(true)}
            className="text-muted mt-2 text-xs underline underline-offset-4"
          >
            Got it
          </button>
        </div>
      )}

      <header className="mb-6">
        <p className="text-muted text-xs uppercase tracking-wide">{method.mealName}</p>
        <div
          className="bg-line mt-3 h-1.5 overflow-hidden rounded-full"
          role="progressbar"
          aria-valuenow={index + 1}
          aria-valuemin={1}
          aria-valuemax={totalCards}
          aria-label="Steps"
        >
          <div
            className="bg-plate-b h-full rounded-full transition-[width] duration-300"
            style={{ width: `${((index + 1) / totalCards) * 100}%` }}
          />
        </div>
        <p className="text-muted mt-2 text-xs">
          {onGather ? (
            "Before you start"
          ) : (
            <>
              Step {index + 1 - offset} of {steps.length}
              {step.minutes ? ` · about ${step.minutes} min` : ""}
            </>
          )}
        </p>
      </header>

      {onGather && <GatherCard gather={method.gather} />}

      {!onGather && (
      <div className="flex-1">
        <StepBadge step={step} />

        {/* Big. This is read from two feet away with something in one hand. */}
        {/* At text-2xl a single long word — Worcestershire — is wider than a
            phone. break-words is what stops it taking the page with it. */}
        <p className="mt-4 break-words text-2xl leading-snug">{step.text}</p>

        {step.ingredients.length > 0 && (
          <div className="border-line mt-6 rounded-xl border p-4">
            <p className="text-muted text-xs uppercase tracking-wide">
              {step.style === "pack" ? "You bought the packet" : "You bought these"}
            </p>
            <ul className="mt-2 flex flex-col gap-1 text-sm">
              {step.ingredients.map((i) => <li key={i} className="break-words">{i}</li>)}
            </ul>

            {/* ⚠️ The override lives HERE, not in a settings list (#1330).
                This line is the moment somebody realises they would rather
                have had the jars — or the packet. Asking anywhere else is
                asking when nobody is thinking about it. */}
            {step.requirementName && <StyleSwitch step={step} />}
          </div>
        )}

        {/* ⚠️ Shown on EVERY step of that dish, not once at the top. Seasoning
            is a thing you do continuously, and a note read before step one is
            a note forgotten by step four. */}
        {step.componentName && method.seasoning[step.componentName] && (
          <div
            className="mt-6 rounded-xl border p-4"
            style={{ borderColor: "var(--color-plate-a)" }}
          >
            <p className="text-plate-a text-xs uppercase tracking-wide">Seasoning</p>
            <p className="mt-1.5 text-sm">{method.seasoning[step.componentName]}</p>
          </div>
        )}

        {step.componentName && (
          <p className="text-muted mt-6 text-xs">{step.componentName}</p>
        )}
      </div>
      )}

      <div className="mt-8 flex gap-3">
        <button
          onClick={() => setIndex(Math.max(0, index - 1))}
          disabled={index === 0}
          className="border-line tap flex-1 rounded-xl border font-medium disabled:opacity-40"
        >
          Back
        </button>
        <button
          onClick={() => setIndex(index + 1)}
          className="bg-plate-b text-ground tap flex-[2] rounded-xl font-semibold"
        >
          {index === steps.length - 1 ? "Done" : "Next"}
        </button>
      </div>
    </div>
  );
}

function StepBadge({ step }: { step: CookStep }) {
  if (step.role === "pivot") {
    return (
      // The step the whole product exists for. It gets the loudest treatment on
      // the screen, because missing it is the one mistake that cannot be undone.
      <span
        className="inline-block max-w-full break-words rounded-lg border-2 px-3 py-1 text-xs font-bold uppercase tracking-wide"
        style={{ color: "var(--color-warn)", borderColor: "var(--color-warn)" }}
      >
        This is where it splits
      </span>
    );
  }
  if (step.role === "fork") {
    return (
      // ⚠️ tracking-wide, not widest, and it wraps: "Only for ANN AND BO AND CY"
      // in letter-spaced caps is a very long line on a narrow phone.
      <span className="text-plate-a block break-words text-xs font-semibold uppercase tracking-wide">
        Only for {step.forPlates.join(" and ")}
      </span>
    );
  }
  return <span className="text-muted text-xs uppercase tracking-widest">Everybody</span>;
}

/**
 * Hold the screen on while a method is open.
 *
 * ⚠️ Re-acquired on visibility change: a wake lock is released whenever the tab
 * is hidden, so answering the door once would otherwise leave the phone sleeping
 * for the rest of dinner.
 */
function useKeepAwake() {
  const lock = useRef<any>(null);

  useEffect(() => {
    const nav = navigator as any;
    if (!nav.wakeLock?.request) return;

    let cancelled = false;
    const acquire = async () => {
      try {
        if (document.visibilityState !== "visible") return;
        lock.current = await nav.wakeLock.request("screen");
      } catch {
        // Denied, unsupported, or a low battery. Nothing to say about it —
        // the recipe still works, the screen just dims like any other page.
      }
    };

    void acquire();
    const onVisible = () => { if (!cancelled) void acquire(); };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisible);
      lock.current?.release?.().catch(() => {});
    };
  }, []);
}

/** The note, written while the pans are still on the side. */
function NoteForNextTime({ mealId, existing }: { mealId: number; existing: string | null }) {
  const queryClient = useQueryClient();
  const [text, setText] = useState(existing ?? "");
  const [saved, setSaved] = useState(false);

  const save = useMutation({
    mutationFn: () => twoPlates.markMeal(mealId, { note: text.trim() }),
    onSuccess: () => {
      setSaved(true);
      queryClient.invalidateQueries({ queryKey: ["method", mealId] });
      queryClient.invalidateQueries({ queryKey: ["deck"] });
    },
  });

  return (
    <div className="border-line mt-8 rounded-xl border p-4">
      <label htmlFor="note" className="text-muted text-xs uppercase tracking-wide">
        Anything to remember for next time?
      </label>
      <p className="text-muted mt-1 text-xs">
        More chili. Half the rice. Sam did not touch the peppers. You will see it
        before you cook this again.
      </p>
      <textarea
        id="note"
        value={text}
        onChange={(e) => { setText(e.target.value); setSaved(false); }}
        rows={3}
        placeholder="Needs way more chili"
        className="bg-surface border-line mt-3 w-full rounded-lg border px-3 py-2 text-base outline-none focus:border-plate-b"
      />
      <div className="mt-2 flex items-center gap-3">
        <button
          onClick={() => save.mutate()}
          disabled={save.isPending}
          className="border-line rounded-lg border px-4 py-2 text-sm font-medium disabled:opacity-50"
        >
          {save.isPending ? "Saving…" : saved ? "Saved" : "Save the note"}
        </button>
        {/* Clearing has to be as easy as writing. A note you cannot unwrite is
            a trap, and the API takes "" for exactly this. */}
        {existing && text.trim() !== "" && (
          <button
            onClick={() => { setText(""); save.mutate(); }}
            className="text-muted text-sm underline underline-offset-4"
          >
            Clear it
          </button>
        )}
      </div>
      {save.isError && (
        <p role="alert" className="text-warn mt-2 text-sm">That did not save. Try again.</p>
      )}
    </div>
  );
}

/** Swap pack for scratch on ONE requirement, from the step that used it. */
function StyleSwitch({ step }: { step: CookStep }) {
  const queryClient = useQueryClient();
  const other = step.style === "pack" ? "scratch" : "pack";
  const [said, setSaid] = useState(false);

  const save = useMutation({
    mutationFn: () => twoPlates.saveStylePreference(step.requirementName!, other),
    onSuccess: () => {
      setSaid(true);
      queryClient.invalidateQueries({ queryKey: ["deck"] });
      queryClient.invalidateQueries({ queryKey: ["style-preferences"] });
    },
  });

  if (said) {
    return (
      <p className="text-plate-b mt-3 text-xs">
        Noted — next time this shops as {other === "pack" ? "a packet" : "the separate ingredients"}.
      </p>
    );
  }

  return (
    <button
      onClick={() => save.mutate()}
      disabled={save.isPending}
      className="text-muted mt-3 text-xs underline underline-offset-4 disabled:opacity-50"
    >
      {save.isPending
        ? "Saving…"
        : other === "scratch"
          ? "Rather make this from scratch next time?"
          : "Rather use a packet next time?"}
    </button>
  );
}
